import { INSTALLATION_SELECT, MAX_BATCH_SIZE, MAX_RETRIES } from 'constants/utils';

export const isNumber = (value: string | number) => {
    return value != null && value !== '' && !isNaN(Number(value.toString()));
};

export const range = (start: number, end: number, step = 1) => {
    return Array.from({ length: Math.floor((end - start) / step) + 1 }, (_, index) => start + index * step);
};

export const getDeviceType = () => {
    const ua = navigator.userAgent;
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
        return 'tablet';
    }
    if (
        /Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)
    ) {
        return 'mobile';
    }
    return 'desktop';
};

export const handleApiErrorResponseForTrack = (error: any) => {
    const responseData = error?.response?.data;
    return responseData ? (responseData as string) : (error as string);
};

export const nameof = <T>(name: keyof T) => name;

export const timeStringToSeconds = (time: string): number => {
    const [hours, minutes, seconds] = time.split(':').map(Number);
    return hours * 3600 + minutes * 60 + seconds;
};

export const secondsToTimeString = (totalSeconds: number): string => {
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = Math.floor(totalSeconds % 60);
    return [h, m, s].map(v => String(v).padStart(2, '0')).join(':');
};

export const weightedAverageTime = (
    existingTime: string,
    existingWeight: number,
    newTime: string,
    newWeight: number,
): string => {
    const existingSeconds = timeStringToSeconds(existingTime);
    const newSeconds = timeStringToSeconds(newTime);
    const effectiveExistingWeight = existingSeconds === 0 ? 0 : existingWeight;
    const effectiveNewWeight = newSeconds === 0 ? 0 : newWeight;
    const totalWeight = effectiveExistingWeight + effectiveNewWeight;
    if (totalWeight === 0) return '00:00:00';
    const weighted = (existingSeconds * effectiveExistingWeight + newSeconds * effectiveNewWeight) / totalWeight;
    return secondsToTimeString(weighted);
};

export const delay = (ms: number) => new Promise(res => setTimeout(res, ms));

export const removeAccents = (text: string) => text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

export const paginate = (array: any[], itemsPerPage: number, page: number) => {
    return array.slice((page - 1) * itemsPerPage, page * itemsPerPage);
};

export const copyArray = (array: any[]) => JSON.parse(JSON.stringify(array));

export const hasDuplicateItens = (array: string[]) => {
    return new Set(array).size !== array.length;
};

export const extractErrorCodeFromPacksApi = (packApiResponse: any) => packApiResponse?.response?.data.Title;

export const filterArray = (array: any[], searchTerm: string) => {
    if (!array) return [];
    const copyData = copyArray(array) as any[];
    let filteredData;
    if (!searchTerm) {
        filteredData = copyData;
    } else {
        filteredData = copyData.filter(item =>
            Object.values(item).some((value: any) =>
                value?.toString().toLowerCase().includes(searchTerm.toLowerCase()),
            ),
        );
    }
    return filteredData;
};

export const filterTicketArray = (array: any, searchTerm: string) => {
    if (!array) return [];
    const copyData: any = copyArray(array.data);
    let filteredData;
    if (!searchTerm) {
        filteredData = copyData;
    } else {
        filteredData = copyData.filter(
            (item: any) =>
                Object.values(item).some((value: any) =>
                    value?.toString().toLowerCase().includes(searchTerm.toLowerCase()),
                ) ||
                item.attendant?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.customer?.name?.toLowerCase().includes(searchTerm.toLowerCase()),
        );
    }
    return filteredData;
};

export const convertDateToLocal = (date: string) => {
    const formattedDate = new Date(Date.parse(date)).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' });
    return formattedDate.replace(',', '');
};

export async function retryBatchRequest<T>(
    api: any,
    methodName: string,
    methodParameters: (items: any[]) => any,
    callback: (result: T, amount: number) => any,
    items: any[],
    batchSize = MAX_BATCH_SIZE,
    maxRetries = MAX_RETRIES,
    attempts = 0,
) {
    const failureItems = [] as any[];
    for (let i = 0; i < items.length; i += batchSize) {
        const batch = items.slice(i, i + batchSize);

        try {
            const response = await api[methodName](...methodParameters(batch));
            callback(response, batch.length);
        } catch (error) {
            failureItems.push(...batch);
        }
    }
    if (attempts >= maxRetries) {
        throw new Error();
    }

    if (failureItems.length > 0) {
        await retryBatchRequest(
            api,
            methodName,
            methodParameters,
            callback,
            failureItems,
            batchSize,
            maxRetries,
            attempts + 1,
        );
    }
}

export function secondsToDHM(seconds: number | undefined): string {
    if (!seconds) return '00:00:00';
    const date = new Date(null as any);
    date.setSeconds(seconds);
    const isoDate = date.toISOString();
    const days = parseInt(isoDate.slice(8, 10)) - 1;
    if (days > 0) {
        return `${days.toString().padStart(2, '0')}d${isoDate.slice(11, 16)}`;
    } else {
        return isoDate.slice(11, 19);
    }
}

export const ActualDate = () => {
    const date: Date = new Date(Date.now() - new Date().getTimezoneOffset() * 60000);
    return `${date.getUTCDate().toString().padStart(2, '0')}/${(date.getUTCMonth() + 1).toString().padStart(2, '0')}/${date.getUTCFullYear()} às ${date.getUTCHours().toString().padStart(2, '0')}:${date.getUTCMinutes().toString().padStart(2, '0')}:${date.getUTCSeconds().toString().padStart(2, '0')}`;
};

export const clearMultiSelect = async () => {
    const installationSelect = document.getElementById(INSTALLATION_SELECT) as HTMLBdsAutocompleteElement;
    await installationSelect?.cleanMultipleSelection();
};
